import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys, type SablonyFilters } from "@/api/queryKeys";
import type {
  DataResponse,
  Sablona,
  SablonaCreate,
  SablonaInsert,
  SablonaInsertResult,
  SablonaSummary,
} from "@/api/schemas";

export function useSablony(
  filters: SablonyFilters = {},
): UseQueryResult<DataResponse<SablonaSummary[]>> {
  return useQuery({
    queryKey: queryKeys.sablony.list(filters),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<SablonaSummary[]>>(
        "/sablony",
        { params: filters },
      );
      return data;
    },
  });
}

export function useSablona(
  id: string | undefined,
): UseQueryResult<DataResponse<Sablona>> {
  return useQuery({
    queryKey: queryKeys.sablony.detail(id ?? ""),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<Sablona>>(
        `/sablony/${id}`,
      );
      return data;
    },
    enabled: Boolean(id),
  });
}

export function useCreateSablona(): UseMutationResult<
  DataResponse<Sablona>,
  Error,
  SablonaCreate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SablonaCreate) => {
      const { data } = await api.post<DataResponse<Sablona>>(
        "/sablony",
        payload,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.sablony.all });
    },
  });
}

export function useDeleteSablona(): UseMutationResult<
  void,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/sablony/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.sablony.all });
    },
  });
}

export function useInsertSablona(
  zakazkaId: string,
): UseMutationResult<
  DataResponse<SablonaInsertResult>,
  Error,
  { sablonaId: string; data: SablonaInsert }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ sablonaId, data }) => {
      const response = await api.post<DataResponse<SablonaInsertResult>>(
        `/sablony/${sablonaId}/insert`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      // Nová zostava + prípadné nové sloty + prepočet cien
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) });
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.all });
      // Vložená zostava mení aj súhrny kovaní a hrán vpravo (KOLO 216)
      qc.invalidateQueries({ queryKey: queryKeys.varianty.kovaniaSummaries() });
      qc.invalidateQueries({ queryKey: queryKeys.varianty.edgesSummaries() });
    },
  });
}
